import { ChangeEvent, useEffect, useState } from 'react'
import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import { cn } from 'shared/styles'

type Props = {
  total: number
  current: number
  pageSize: number
  onChange: (currentPage: number, items: number) => void
  onShowSizeChange: (page: number, newSize: number) => void | undefined
}

export const Pagination = ({
  total,
  current,
  pageSize,
  onChange,
  onShowSizeChange,
}: Props) => {
  const [page, setPage] = useState(current)
  const totalPages = Math.ceil(total / pageSize)

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [page, pageSize])

  const handleSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(event.target.value, 10)

    onShowSizeChange(page, newSize)
  }

  const handleChangePage = (curPage: number) => {
    setPage(curPage)
    onChange(curPage, pageSize)
  }

  const renderButton = (num: number) => (
    <button
      key={num}
      className={cn(
        'min-w-10 rounded-md border bg-white p-2 text-black',
        page === num
          ? 'border-blue-500 bg-blue-400! text-white!'
          : 'hover:border-blue-500 hover:bg-blue-300 hover:text-white',
      )}
      onClick={() => {
        setPage(num)
        onChange(num, pageSize)
      }}
    >
      {num}
    </button>
  )

  const renderPageButtons = () => {
    const buttons = []

    if (page < 5) {
      for (let i = 2; i <= Math.min(5, totalPages); i++) {
        buttons.push(renderButton(i))
      }
      buttons.push(
        <EllipsisHorizontalIcon
          key='ellipsis_1'
          className='size-6 stroke-2'
        />,
      )
    } else if (page >= 5 && page <= totalPages - 4) {
      if (page > 2) {
        buttons.push(
          <EllipsisHorizontalIcon
            key='ellipsis_2'
            className='size-6 stroke-2'
          />,
        )
      }
      for (let i = page - 2; i <= page + 2; i++) {
        if (i > 1 && i < totalPages) {
          buttons.push(renderButton(i))
        }
      }
      if (page < totalPages - 1) {
        buttons.push(
          <EllipsisHorizontalIcon
            key='ellipsis_3'
            className='size-6 stroke-2'
          />,
        )
      }
    } else {
      buttons.push(
        <EllipsisHorizontalIcon
          key='ellipsis_4'
          className='size-6 stroke-2'
        />,
      )
      for (let i = totalPages - 4; i < totalPages; i++) {
        buttons.push(renderButton(i))
      }
    }

    return buttons
  }

  return (
    <div className='mt-3 flex items-center justify-end gap-3'>
      <div className='flex items-center gap-1'>
        <button
          className={cn(
            'mb-px rounded-md border p-2',
            page !== 1 &&
              'bg-white text-black hover:border-blue-500 hover:bg-blue-300 hover:text-white',
          )}
          onClick={() => handleChangePage(page - 1)}
          disabled={page === 1}
        >
          <ChevronRightIcon className='size-5 rotate-180 stroke-2' />
        </button>
        {renderButton(1)}
        {renderPageButtons()}
        {renderButton(totalPages)}
        <button
          className={cn(
            'mb-px rounded-md border p-2',
            page !== totalPages &&
              'bg-white text-black hover:border-blue-500 hover:bg-blue-300 hover:text-white',
          )}
          onClick={() => handleChangePage(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRightIcon className='size-5 stroke-2' />
        </button>
      </div>
      <select
        onChange={handleSizeChange}
        value={pageSize}
        className='base-input'
      >
        <option value={10}>10/page</option>
        <option value={20}>20/page</option>
        <option value={50}>50/page</option>
        <option value={100}>100/page</option>
      </select>
    </div>
  )
}
